<script setup lang="ts">
  import { IItem } from '@/interfaces/IItem';
  import { Globals } from '@/modules/Globals';
  import { computed, ref } from 'vue';

  type Size =
    | 'xs'
    | 'sm'
    | 'md'
    | 'lg'
    | 'xl'
    | '2xl'
    | '3xl'
    | '4xl'
    | '5xl'
    | '6xl';

  const props = withDefaults(
    defineProps<{
      item?: IItem | null;
      size?: Size;
      placeholderSize?: Size;
    }>(),
    {
      size: 'lg',
    }
  );

  const logoPath = computed(() => {
    if (!props.item?.picture) return undefined;

    return `${Globals.uploadsPath}/${props.item.picture}`;
  });

  const sizes = {
    xs: 24,
    sm: 32,
    md: 48,
    lg: 64,
    xl: 96,
    '2xl': 128,
    '3xl': 192,
    '4xl': 256,
    '5xl': 384,
    '6xl': 512,
  };

  const size = computed(() => sizes[props.size]);

  const placeholder = computed(() =>
    !props.placeholderSize
      ? undefined
      : [
          sizes[props.placeholderSize!],
          sizes[props.placeholderSize!],
          undefined,
          -1,
        ]
  );

  const errorLoading = ref(false);
</script>

<template>
  <v-img
    v-if="logoPath && !errorLoading"
    class="store-logo"
    :src="logoPath"
    :alt="`Foto de ${item?.name}`"
    :width="size"
    :height="size"
    provider="cloudflare"
    fit="cover"
    loading="lazy"
    :placeholder="placeholder"
    @error="errorLoading = true"
  />
  <div
    v-else
    class="placeholder"
    :style="{
      width: size + 'px',
      height: size + 'px',
    }"
  >
    <v-icon class="icon">mdi-food</v-icon>
  </div>
</template>

<style scoped lang="scss">
  .store-logo {
    @apply tw-rounded-md tw-max-w-full tw-max-h-full tw-object-contain;
  }

  .placeholder {
    @apply tw-flex tw-items-center tw-justify-center tw-bg-gray-100;
    @apply tw-rounded-md tw-max-w-full tw-max-h-full tw-object-contain;

    .icon {
      @apply tw-text-gray-300 tw-text-5xl;
    }
  }
</style>
